// =============================================================================
// lib/lifeProfile.js — Phase 74A (post-user-feedback voice pass)
//
// Canonical catalog of Life Profile questions. Mirrors the FIELD_CATALOG
// in /app/backend/routes/life_profile.py — new fields must be added in
// both places (backend for completeness scoring; frontend for the form).
//
// This file is the SOURCE OF TRUTH for the frontend:
//   • The Life Profile Settings page renders these top-to-bottom.
//   • The post-signup mini-flow ("Get to know you") uses the subset
//     flagged `mini_flow: true`.
//   • The reusable <LifeProfilePrompt> component can render any single
//     question from here as an inline prompt (e.g. during goal creation).
//
// Voice principles (post-user-feedback):
//   • Warm, curious, companion-like — never HR/survey/gov-form.
//   • Second person, direct, short.
//   • Every question includes "Prefer not to say" as an equal, dignified
//     option — and PNTS counts as *answered* for completion.
//   • Never > 6 chips per question; if we need more, split.
// =============================================================================

// The sentinel string used by both frontend and backend for "declined
// to answer". Store it verbatim in the JSONB.
export const PNTS = 'prefer_not_to_say';

// Grouped catalog. Each `section.questions` is an ordered list rendered
// as its own card on the Life Profile page.
export const LIFE_PROFILE_CATALOG = [
  // ---------------------------------------------------------------------------
  // 1) Your household
  // ---------------------------------------------------------------------------
  {
    id: 'household',
    title: 'Your household',
    subtitle: 'The people around you shape your financial priorities.',
    questions: [
      {
        key: 'household_type',
        prompt: 'Which best describes you today?',
        kind: 'single_choice',
        mini_flow: true,
        options: [
          { value: 'single',   label: 'Single' },
          { value: 'married',  label: 'Married' },
          { value: 'partner',  label: 'With a partner' },
          { value: 'widowed',  label: 'Widowed' },
          { value: 'divorced', label: 'Divorced' },
          { value: PNTS,       label: 'Prefer not to say' },
        ],
      },
      {
        key: 'children_ages',
        prompt: 'Do you have children?',
        helper: 'Their ages help us plan for future education costs.',
        // A custom widget: user starts with "None" or adds age chips (0–25).
        kind: 'age_chips',
        mini_flow: true,
        allow_pnts: true,
      },
      {
        key: 'other_dependents_type',
        prompt: 'Does anyone rely on you financially?',
        kind: 'single_choice',
        options: [
          { value: 'none',            label: 'No one else' },
          { value: 'elderly_parents', label: 'Elderly parent(s)' },
          { value: 'sibling',         label: 'A sibling' },
          { value: 'other_family',    label: 'Other family' },
          { value: PNTS,              label: 'Prefer not to say' },
        ],
      },
      {
        key: 'partner_finances',
        prompt: 'How do you and your partner handle money?',
        kind: 'single_choice',
        depends_on: { key: 'household_type', in: ['married', 'partner'] },
        options: [
          { value: 'together',            label: 'Managed together' },
          { value: 'separately',          label: 'Separately' },
          { value: 'partner_doesnt_earn', label: "My partner doesn't earn" },
          { value: PNTS,                  label: 'Prefer not to say' },
        ],
      },
      {
        key: 'partner_uses_2120',
        prompt: 'Is your partner on 2120?',
        kind: 'single_choice',
        depends_on: { key: 'household_type', in: ['married', 'partner'] },
        options: [
          { value: 'yes',       label: 'Yes' },
          { value: 'not_yet',   label: "Not yet — I'd like to invite them" },
          { value: 'no',        label: 'No' },
          { value: PNTS,        label: 'Prefer not to say' },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // 2) How you earn (Employment & income)
  // ---------------------------------------------------------------------------
  {
    id: 'employment',
    title: 'How you earn',
    subtitle: 'Understanding how you earn helps us make better projections.',
    questions: [
      {
        key: 'income_stability',
        prompt: 'How predictable is your income?',
        kind: 'single_choice',
        mini_flow: true,
        options: [
          { value: 'fixed_salary',                label: 'Fixed monthly salary' },
          { value: 'mostly_fixed_with_bonuses',   label: 'Mostly fixed, with bonuses' },
          { value: 'variable',                    label: 'Variable month to month' },
          { value: 'seasonal',                    label: 'Seasonal' },
          { value: 'no_income',                   label: 'No income right now' },
          { value: PNTS,                          label: 'Prefer not to say' },
        ],
      },
      {
        key: 'employment_stability',
        prompt: 'Which best describes your work today?',
        kind: 'single_choice',
        options: [
          { value: 'established',   label: "I've been at this for 2+ years" },
          { value: 'new_job',       label: 'New job (under 1 year)' },
          { value: 'contract',      label: 'Contract-based' },
          { value: 'between_jobs',  label: 'Looking for work' },
          { value: PNTS,            label: 'Prefer not to say' },
        ],
      },
      {
        key: 'multiple_income_sources',
        prompt: 'Do you have more than one source of income?',
        kind: 'single_choice',
        options: [
          { value: 'yes',    label: 'Yes' },
          { value: 'no',     label: 'Just one' },
          { value: PNTS,     label: 'Prefer not to say' },
        ],
      },
      {
        key: 'business_revenue_predictability',
        prompt: 'How predictable is your business revenue?',
        kind: 'single_choice',
        // Only shown to business owners / freelancers / self-employed.
        depends_on: { key_profile: 'employment_type', in: ['Self-employed', 'Freelancer', 'Business owner'] },
        options: [
          { value: 'recurring',    label: 'Recurring / subscription' },
          { value: 'project_based', label: 'Project-based' },
          { value: 'one_time',     label: 'Mostly one-time deals' },
          { value: 'mixed',        label: 'A mix' },
          { value: PNTS,           label: 'Prefer not to say' },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // 3) Your home (Home & commitments)
  // ---------------------------------------------------------------------------
  {
    id: 'housing',
    title: 'Your home',
    subtitle: 'Your home and commitments help us plan more accurately.',
    questions: [
      {
        key: 'housing_status',
        prompt: "What's your current housing situation?",
        kind: 'single_choice',
        mini_flow: true,
        options: [
          { value: 'rent',              label: 'I rent' },
          { value: 'own_with_mortgage', label: 'I own (with a mortgage)' },
          { value: 'own_outright',      label: 'I own outright' },
          { value: 'with_family',       label: 'Living with family' },
          { value: 'company_provided',  label: 'Company-provided' },
          { value: PNTS,                label: 'Prefer not to say' },
        ],
      },
      {
        key: 'vehicle_count',
        prompt: 'How many vehicles in your household?',
        kind: 'single_choice',
        options: [
          { value: 0,       label: 'None' },
          { value: 1,       label: 'One' },
          { value: 2,       label: 'Two' },
          { value: 3,       label: 'Three or more' },
          { value: PNTS,    label: 'Prefer not to say' },
        ],
      },
      {
        key: 'health_insurance',
        prompt: 'Health insurance',
        kind: 'single_choice',
        options: [
          { value: 'self',     label: 'I have my own' },
          { value: 'employer', label: 'Covered through employer' },
          { value: 'family',   label: 'Covered by family' },
          { value: 'none',     label: 'Not currently insured' },
          { value: PNTS,       label: 'Prefer not to say' },
        ],
      },
      {
        key: 'life_insurance',
        prompt: 'Life insurance',
        kind: 'single_choice',
        options: [
          { value: 'yes',       label: 'Yes' },
          { value: 'no',        label: 'No' },
          { value: 'not_sure',  label: 'Not sure' },
          { value: PNTS,        label: 'Prefer not to say' },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // 4) Your future (Investing & retirement)
  // ---------------------------------------------------------------------------
  {
    id: 'future',
    title: 'Your future',
    subtitle: 'Help us understand how you approach investing and the future.',
    questions: [
      {
        key: 'risk_appetite',
        prompt: 'How would you describe your appetite for investment risk?',
        kind: 'single_choice',
        options: [
          { value: 'conservative', label: 'Conservative' },
          { value: 'balanced',     label: 'Balanced' },
          { value: 'growth',       label: 'Growth-oriented' },
          { value: 'aggressive',   label: 'Aggressive' },
          { value: 'not_sure',     label: 'Not sure yet' },
          { value: PNTS,           label: 'Prefer not to say' },
        ],
      },
      {
        key: 'investment_familiarity',
        prompt: 'How familiar are you with investing?',
        kind: 'single_choice',
        options: [
          { value: 'never',       label: "I've never invested" },
          { value: 'starting',    label: 'Just getting started' },
          { value: 'few_years',   label: 'A few years in' },
          { value: 'experienced', label: 'Experienced' },
          { value: PNTS,          label: 'Prefer not to say' },
        ],
      },
      // v3.7.11 Phase 84.31 Part 5 — Behavioral investing scenario.
      // The single-label "risk appetite" question above is a poor
      // predictor of real behaviour. Combined with `investing_shock_reaction`,
      // runway, horizon and debt, we build a much more honest "investing
      // context" (see backend/routes/profile_summary.py). The prompt uses
      // a large round number in the user's display currency so it feels
      // real; the amount is illustrative only, never anchored to the
      // user's actual portfolio.
      {
        key: 'investing_shock_reaction',
        prompt: 'If an investment worth 100,000 in your display currency temporarily fell to 75,000, what would you most likely do?',
        kind: 'single_choice',
        hint:  'This is one input among several — runway, horizon and debt matter more than any single answer.',
        options: [
          { value: 'sell_all',    label: 'Sell everything to protect what remains' },
          { value: 'sell_some',   label: 'Sell some to reduce the risk' },
          { value: 'hold',        label: 'Hold and wait it out' },
          { value: 'buy_more',    label: 'Buy more while prices are lower' },
          { value: 'not_sure',    label: "I'm not sure — I've never faced this" },
          { value: PNTS,          label: 'Prefer not to say' },
        ],
      },
      {
        key: 'retirement_age_target',
        prompt: 'When would you like the option to stop working?',
        kind: 'single_choice',
        options: [
          { value: 45,    label: 'By 45' },
          { value: 50,    label: 'By 50' },
          { value: 55,    label: 'By 55' },
          { value: 60,    label: 'By 60' },
          { value: 65,    label: 'By 65' },
          { value: 70,    label: 'By 70+' },
          { value: 'unknown', label: "I don't know yet" },
          { value: PNTS,  label: 'Prefer not to say' },
        ],
      },
      {
        key: 'retirement_lifestyle',
        prompt: 'What kind of lifestyle would you like in retirement?',
        kind: 'single_choice',
        options: [
          { value: 'frugal',         label: 'Frugal — the essentials' },
          { value: 'comfortable',    label: 'Comfortable' },
          { value: 'aspirational',   label: 'Aspirational — travel, hobbies' },
          { value: 'same_as_today',  label: 'Same as today' },
          { value: 'not_sure',       label: 'Not sure yet' },
          { value: PNTS,             label: 'Prefer not to say' },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // 5) Your priorities (What matters most)
  // ---------------------------------------------------------------------------
  {
    id: 'priorities',
    title: 'Your priorities',
    subtitle: "Tell us what's most important right now.",
    questions: [
      {
        key: 'top_intents',
        prompt: 'Pick up to three that matter most right now.',
        kind: 'multi_choice_ranked',
        mini_flow: true,
        max_selected: 3,
        options: [
          { value: 'debt_free',       label: 'Become debt-free' },
          { value: 'emergency_fund',  label: 'Build an emergency fund' },
          { value: 'buy_home',        label: 'Buy a home' },
          { value: 'kids_education',  label: "Save for children's education" },
          { value: 'retire_early',    label: 'Retire early' },
          { value: 'financial_independence', label: 'Reach financial independence' },
          { value: 'reduce_stress',   label: 'Reduce money stress' },
          { value: 'grow_investments', label: 'Grow investments' },
          { value: 'legacy',          label: 'Leave a legacy' },
        ],
      },
      {
        key: 'biggest_worry',
        prompt: "What's your biggest financial concern today?",
        kind: 'single_choice',
        options: [
          { value: 'overspending',       label: 'Overspending' },
          { value: 'debt',               label: 'Debt' },
          { value: 'no_cushion',         label: 'No safety cushion' },
          { value: 'not_saving_enough',  label: "I'm not saving enough" },
          { value: 'investment_losses',  label: 'Investment losses' },
          { value: 'medical_expenses',   label: 'Unexpected medical expenses' },
          { value: 'job_security',       label: 'Job security' },
          { value: 'family_pressure',    label: 'Family pressure' },
          { value: 'none',               label: 'None of these' },
          { value: PNTS,                 label: 'Prefer not to say' },
        ],
      },
      {
        key: 'financial_confidence',
        prompt: 'How do you feel about your finances today?',
        kind: 'emoji_scale',
        // 1 = stressed, 5 = fully in control (mapped to emojis in the UI)
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // 6) Country-specific (nested)
  // ---------------------------------------------------------------------------
  {
    id: 'country_context',
    title: 'A few country-specific questions',
    subtitle: 'These help us give tax- and account-aware suggestions later.',
    questions: [
      {
        key: 'country_context.tax_regime',
        prompt: 'Which tax regime do you use?',
        kind: 'single_choice',
        depends_on: { key_profile: 'country', in: ['India'] },
        options: [
          { value: 'old',   label: 'Old regime' },
          { value: 'new',   label: 'New regime' },
          { value: 'unknown', label: "Don't know" },
          { value: PNTS,    label: 'Prefer not to say' },
        ],
      },
      {
        key: 'country_context.retirement_accounts_in',
        prompt: 'Which retirement accounts do you have? (India)',
        kind: 'multi_choice',
        depends_on: { key_profile: 'country', in: ['India'] },
        options: [
          { value: 'epf',   label: 'EPF / PF' },
          { value: 'nps',   label: 'NPS' },
          { value: 'ppf',   label: 'PPF' },
          { value: 'none',  label: 'None yet' },
          { value: PNTS,    label: 'Prefer not to say' },
        ],
      },
      {
        key: 'country_context.retirement_accounts_us',
        prompt: 'Which retirement accounts do you have? (US)',
        kind: 'multi_choice',
        depends_on: { key_profile: 'country', in: ['United States'] },
        options: [
          { value: '401k',      label: '401(k)' },
          { value: 'trad_ira',  label: 'Traditional IRA' },
          { value: 'roth_ira',  label: 'Roth IRA' },
          { value: 'hsa',       label: 'HSA' },
          { value: 'none',      label: 'None yet' },
          { value: PNTS,        label: 'Prefer not to say' },
        ],
      },
    ],
  },
];

// -----------------------------------------------------------------------------
// Small helpers
// -----------------------------------------------------------------------------
export function isMiniFlowQuestion(q) {
  return !!q?.mini_flow;
}

export function getMiniFlowQuestions() {
  const out = [];
  for (const section of LIFE_PROFILE_CATALOG) {
    for (const q of section.questions) {
      if (isMiniFlowQuestion(q)) out.push({ section: section.id, ...q });
    }
  }
  return out;
}

// Nested-key aware set/get so the top_intents / country_context.* keys
// can be edited by the same LifeProfilePrompt component.
export function getValue(bag, key) {
  if (!key.includes('.')) return bag?.[key];
  return key.split('.').reduce((acc, p) => (acc == null ? acc : acc[p]), bag);
}

export function setValue(bag, key, value) {
  const next = { ...(bag || {}) };
  if (!key.includes('.')) {
    if (value === undefined) delete next[key];
    else next[key] = value;
    return next;
  }
  const parts = key.split('.');
  let cursor = next;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const p = parts[i];
    cursor[p] = { ...(cursor[p] || {}) };
    cursor = cursor[p];
  }
  const leaf = parts[parts.length - 1];
  if (value === undefined) delete cursor[leaf];
  else cursor[leaf] = value;
  return next;
}

// Should we show this question? Runs dependency checks against the
// current life_profile bag AND (optionally) the user's core profile.
export function shouldShowQuestion(q, { life_profile, profile }) {
  const dep = q?.depends_on;
  if (!dep) return true;
  if (dep.key) {
    const v = getValue(life_profile || {}, dep.key);
    if (dep.in && !dep.in.includes(v)) return false;
  }
  if (dep.key_profile) {
    const v = profile?.[dep.key_profile];
    if (dep.in && !dep.in.includes(v)) return false;
  }
  return true;
}
